import isArray from 'lodash-es/isArray';
import WebFont from 'webfontloader';

/**
 * For custom fonts, we need a css declaration
 *
 * @param font
 */
const createCustomFontStyle = font => {
  font.variants.forEach(variant => {
    const style = document.createElement('style');
    style.type = 'text/css';
    style.innerHTML = `@font-face { font-family: '${font.name}'; src: url('${
      variant.url
    }') format('woff'); font-weight: ${variant.weight ||
      400}; font-display:block; font-style: ${variant.style || 'normal'}; })`;
    document.getElementsByTagName('head')[0].appendChild(style);
  });
};

/**
 * WebFont loader expects FVD format for custom fonts
 * see https://github.com/typekit/webfontloader#custom
 *
 * @param font
 * @return {string}
 */
const toFVD = font => {
  const variantParts = font.variants
    .filter(variant => variant.name !== 'Default')
    .map(variant => variant.style[0] + variant.weight[0])
    .join(',');

  return `${font.name}${variantParts.length > 0 ? `:${variantParts}` : ''}`;
};

/**
 * Google format for webfont config
 *
 * @param font
 * @return {string}
 */
const toGoogle = font =>
  `${font.name}${
    font.variants.length > 0
      ? `:${font.variants.map(variant => variant.weight).join(',')}`
      : ''
  }`;

/**
 * Generate a font name based on weight and style
 * This is only used when adding a google font
 *
 * @param fontWeight
 * @param fontStyle
 * @return {string}
 *
 * @todo: remove once google fonts are added with GraphQL (should return the correct font name)
 */
export const generateFontName = (fontWeight, fontStyle) => {
  let name = '';

  switch (fontWeight) {
    case '100':
      name += 'Thin';
      break;
    case '200':
      name += 'Extra-Light';
      break;
    case '300':
      name += 'Light';
      break;
    case '400':
      name += 'Regular';
      break;
    case '500':
      name += 'Medium';
      break;
    case '600':
      name += 'Semi-Bold';
      break;
    case '700':
      name += 'Bold';
      break;
    case '800':
      name += 'Extra-Bold';
      break;
    case '900':
      name += 'Black';
      break;
    default:
      break;
  }

  if (fontStyle === 'italic') {
    name += ' Italic';
  }

  return name;
};

/**
 * Load webfonts with Webfont loader - in addition to loading the font, this
 * function will append the appropriate <style> elements to the document
 *
 * @param fontOrFonts a single font object of an array of font objects
 * @param config (additional webfont loader config)
 * @param isOffline
 * @return {Promise<any>}
 */
export const loadWebFonts = (fontOrFonts, config = {}, isOffline = false) => {
  const fonts = isArray(fontOrFonts) ? fontOrFonts : [fontOrFonts];

  return new Promise((resolve, reject) => {
    // Append style elements to document (not needed if offline)
    if (!isOffline) {
      fonts
        .filter(f => f.type === 'custom' || f.type === 'featured')
        .forEach(createCustomFontStyle);
    }

    // Webfont config
    const fontConfig = {
      ...config,
      active: () => {
        resolve();
      },
      inactive: () => {
        reject(new Error('Error while loading fonts'));
      },
    };

    // Transform google fonts in Webfont-compatible families (unless in offline mode)
    const googleFontFamilies = fonts
      .filter(f => !isOffline && f.type === 'google')
      .map(toGoogle);
    if (googleFontFamilies.length > 0) {
      fontConfig.google = { families: googleFontFamilies };
    }

    // Transform custom (& featured) fonts in Webfont-compatible families
    // Note that in offline mode, we consider google fonts as custom fonts
    const customFontFamilies = fonts
      .filter(
        f =>
          f.type === 'custom' ||
          f.type === 'featured' ||
          (isOffline && f.type === 'google'),
      )
      .map(toFVD);
    if (customFontFamilies.length > 0) {
      fontConfig.custom = { families: customFontFamilies };
    }

    fontConfig.timeout = 20000;
    // Load!
    WebFont.load(fontConfig);
  });
};
